<script setup lang="ts">
import { computed, ComputedRef } from "vue";

import { useApplicationStore } from "../../../../stores/application";
import { LINKS } from "../../../../constants/links";
import { getImageUrl } from "../../../../utils";

const applicationStore = useApplicationStore();

const annuityDocumentsLinks: ComputedRef<{ title: string; link: string; }[]> = computed(() => [
    {
        title: "Cover letter",
        link: `/documents/${applicationStore.quoteNumber}/application/${applicationStore.applicationId}/pdf/cover-letter`,
    },
    {
        title: "Demands & needs statement",
        link: `/demands-and-needs/${applicationStore.quoteNumber}`,
    },
    {
        title: "Our services",
        link: "/our-services",
    },
    {
        title: "Privacy Notice",
        link: LINKS.ANNUITY_PRIVACY_POLICY,
    },
    {
        title: "Terms & conditions",
        link: LINKS.ANNUITY_TERMS_AND_CONDITIONS,
    },
]);

const downloadAndPrintCellsArray: ComputedRef<{ title: string; link: string; image: string; }[]> = computed(() => [
    {
        title: "Application form",
        link: `/documents/${applicationStore.quoteNumber}/application/${applicationStore.applicationId}/pdf/application-form`,
        image: "apply-app.svg",
    },
    {
        title: "Annuity health form",
        link: `/documents/${applicationStore.quoteNumber}/application/${applicationStore.applicationId}/pdf/medical`,
        image: "apply-med.svg",
    },
    {
        title: "Quote (Key facts)",
        link: `/documents/${applicationStore.quoteNumber}/${applicationStore.premiumId}/keyfacts/pdf`,
        image: "apply-facts.svg",
    },
    {
        title: "Key features",
        link: applicationStore.keyFeaturesUrl,
        image: "apply-features.svg",
    },
    {
        title: `${applicationStore.companyName}'s terms & conditions`,
        link: applicationStore.termsConditionsUrl,
        image: "apply-terms.svg",
    },
    {
        title: `${applicationStore.companyName}'s Privacy Notice`,
        link: applicationStore.privacyUrl,
        image: "apply-privacy.svg",
    },
]);
</script>

<template data-test="downloadAndPrint">
    <div class="row">
        <div class="col-12 float-left mt-3">
            <h2 class="text-secondary blue">
                Download, review, sign and return your application
            </h2>
            <h5>
                Please download, print and review the following documents then return your signed application and annuity health form to us.
            </h5>
            <h5>
                These documents need to be downloaded now for your records as you will not have another opportunity to do so.
            </h5>
        </div>

        <div class="col-lg-12 float-left mt-4 row justify-content-around">
            <div
                v-for="cell in downloadAndPrintCellsArray"
                :key="cell.title"
                class="col-lg-5 float-left bg-white p-4 mb-5 padding-app row"
            >
                <div class="col-md-3 float-left px-0">
                    <img
                        :src="getImageUrl(cell.image)"
                        :alt="cell.title"
                        class="img-fluid-2"
                    >
                </div>
                <div class="col-md-9 float-left text-center-mobile vertical-mid">
                    <span class="line-height-intro">
                        {{ cell.title }}
                    </span>
                    <br>
                    <a
                        class="download"
                        :href="cell.link"
                        target="_blank"
                    >
                        View
                    </a>
                </div>
            </div>
        </div>

        <div class="col-lg-12 ml-auto">
            <p>
                We’ve sent you an e-mail with your demands &amp; needs statement,
                together with our Privacy Notice, terms &amp; conditions and our services documents.
                This e-mail may take a few minutes to arrive.
            </p>
            <p class="pt-3">
                In the meantime, you can view and download these below.
            </p>

            <div
                v-for="link in annuityDocumentsLinks"
                :key="link.title"
                class="col-lg-12 px-0 my-3"
            >
                <span class="line-height-intro">
                    {{ link.title }}
                </span>
                <br>
                <a
                    class="download"
                    :href="link.link"
                    target="_blank"
                >
                    View
                </a>
            </div>
        </div>
    </div>
</template>
